'use client';

import { AppBar, Toolbar, Typography, Box, IconButton, Chip } from '@mui/material';
import { AccountCircle as AccountCircleIcon, Menu as MenuIcon, Help as HelpIcon } from '@mui/icons-material';
import { useState, MouseEvent } from 'react';
import Image from 'next/image';
import NavigationMenu from './menus/NavigationMenu';
import SettingsMenu from './menus/SettingsMenu';
import ProfileMenu from './menus/ProfileMenu';
import HelpMenu from './menus/HelpMenu';
import { useAuthStore } from '@/stores/authStore';
import { log } from '@/lib/utils/logger';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useSnackbar } from '@/hooks/useSnackbar';

export default function NavBar() {
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [navAnchor, setNavAnchor] = useState<null | HTMLElement>(null);
  const [helpAnchor, setHelpAnchor] = useState<null | HTMLElement>(null);

  // Auth context hook for handling authentication
  const { login, error, currentUser } = useAuthStore();
  const systemConfig = useSystemConfig();
  const { showSnackbar } = useSnackbar();

  // Return null or loading state if config is not initialized
  if (!systemConfig) {
    return null;
  }

  // Consolidated menu handler
  const handleMenuClick =
    (setter: React.Dispatch<React.SetStateAction<null | HTMLElement>>) => (event: MouseEvent<HTMLElement>) => {
      setter(event.currentTarget);
    };

  // Close all menus
  const handleClose = () => {
    setNavAnchor(null);
    setSettingsAnchor(null);
    setProfileAnchor(null);
    setHelpAnchor(null);
  };

  const envLabel = systemConfig.environment?.trim();
  const isProdEnv = !!envLabel && /^prod(uction)?$/i.test(envLabel);
  const showEnvironment = !!envLabel && !isProdEnv;

  const isImpersonating = currentUser?.usingSignInAs ?? false;
  const activeRole = currentUser?.roles.find((r) => r.id === currentUser.selectedRole);
  const isAdminRoleActive = currentUser?.selectedRole === 0;

  // Handle user login with error handling and API test
  const handleLogin = async () => {
    const initialError = error; // Store initial error state
    log.info('[NavBar] Starting login process...');
    await login();

    // Check if a new error occurred during login
    if (error && error !== initialError) {
      log.error('[NavBar] Login process failed:', error);
      showSnackbar(`Login failed: ${error}`, 'error');
    } else if (!error) {
      log.info('[NavBar] Login successful');
      showSnackbar('Signed in successfully', 'success');
    }
  };

  // Handle sign-out completion (redirect flows may unload before this runs)
  const handleSignOutComplete = () => {
    if (!error) {
      log.info('[NavBar] Sign-out successful');
      showSnackbar('Signed out successfully', 'success');
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor:
            isAdminRoleActive && isProdEnv ? 'error.main' : systemConfig.theme?.primaryMain || 'primary.main',
        }}
      >
        <Toolbar>
          {/* Left side: Navigation menu and logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              onClick={handleMenuClick(setNavAnchor)}
              size="large"
              aria-label="navigation menu"
              aria-controls="nav-menu"
              aria-haspopup="true"
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="div"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.secondary',
                  mr: 1,
                }}
              >
                {systemConfig.orgName}
              </Typography>
              <Image src={'/images/list_logo.png'} alt={'Lifetime Sample Tracking'} width={110} height={50} priority />
            </Box>
            {/* Conditionally render environment label from server config (non-production only) */}
            {showEnvironment ? (
              <Typography
                variant="body2"
                sx={{
                  marginLeft: 2,
                  color: 'orange',
                  fontWeight: 'bold',
                  '& span': {
                    color: 'lightblue',
                    fontStyle: 'italic',
                  },
                }}
              >
                Environment: <span>{envLabel}</span>
              </Typography>
            ) : null}
          </Box>

          {/* Right side: NSF logo, settings, and profile */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}>
            {/* Impersonation and admin role indicators */}
            {isImpersonating ? (
              <Chip label="SignInAs active" color="warning" size="small" sx={{ fontWeight: 'bold' }} />
            ) : null}
            {isAdminRoleActive ? (
              <Chip
                label={activeRole ? `Role: ${activeRole.name}` : 'Admin role active'}
                color="error"
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            ) : null}

            {systemConfig.logoPath ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={systemConfig.logoPath}
                alt={`${systemConfig.orgName} Logo`}
                height={40}
                style={{ width: 'auto' }}
              />
            ) : null}

            <IconButton
              color="inherit"
              onClick={handleMenuClick(setProfileAnchor)}
              size="large"
              aria-label="profile menu"
              aria-controls="profile-menu"
              aria-haspopup="true"
            >
              <AccountCircleIcon />
            </IconButton>

            <IconButton
              color="inherit"
              onClick={handleMenuClick(setHelpAnchor)}
              size="large"
              aria-label="Open help and user guide menu"
              aria-controls="help-menu"
              aria-haspopup="true"
            >
              <HelpIcon />
            </IconButton>

            {/* Menu Components */}
            <NavigationMenu anchorEl={navAnchor} onClose={handleClose} />

            <SettingsMenu anchorEl={settingsAnchor} onClose={handleClose} />

            <ProfileMenu
              anchorEl={profileAnchor}
              onClose={handleClose}
              onLogin={handleLogin}
              onSignOutComplete={handleSignOutComplete}
            />

            <HelpMenu anchorEl={helpAnchor} onClose={handleClose} />
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
}
